import React, { useMemo } from 'react'
import { Grid, Box, Paper, Typography } from '@mui/material'
import { Bar, Pie } from 'react-chartjs-2'
import { operatorReportStyles as opStyles } from '../../../helpers/style'
import { greenTheme } from '../../../helpers/style'
import { reportFromFacets, directionCountsFromStatusFacet } from './reportFacets'
import { CALL_TYPES, CALL_DIRECTIONS, normalizeDirection } from '../../../helpers/demo'

function mapDocToCall(doc) {
  const main = doc.items?.find((i) => i.uniqueid === doc.linkedid) || doc.items?.[0]
  if (!main) return null
  return {
    start: main.start,
    direction: main.status || main.direction || (main.line && main.line !== 'null' ? 'outgoing' : 'incoming'),
  }
}

export default function ReportCharts({
  report,
  pieChartData: pieChartDataProp,
  barChartData: barChartDataProp,
  directionChartData: directionChartDataProp,
  opTr,
  langIndex,
  onlySection,
  historyData,
  historyFacetFields,
  graphicStatusData,
  graphicDispositionData,
  historyGraphicLoading,
  historyGraphicError,
}) {
  const showAll = !onlySection
  const showDirections = showAll || onlySection === 'directions'

  const facetFields = historyFacetFields ?? historyData?.facet_counts?.facet_fields ?? null
  const displayReport = useMemo(
    () => (report ? reportFromFacets(report, facetFields) : null),
    [report, facetFields]
  )

  const pieChartData = useMemo(() => {
    if (!displayReport) return pieChartDataProp
    const data = [
      displayReport.answered || 0,
      displayReport.answeredLater || 0,
      displayReport.notAnswered || 0,
      displayReport.busy || 0,
      displayReport.failed || 0,
      displayReport.canceled || 0,
    ]
    const labels = CALL_TYPES.slice(0, 6).map((t) => opTr[t.labelKey]?.[langIndex] ?? t.key)
    const colors = CALL_TYPES.slice(0, 6).map((t) => t.color)
    return {
      labels,
      datasets: [{ data, backgroundColor: colors.map((c) => c + 'cc'), borderColor: colors, borderWidth: 2 }],
    }
  }, [displayReport, pieChartDataProp, opTr, langIndex])

  const callsForCharts = useMemo(() => {
    const docs = historyData?.response?.docs
    if (docs && Array.isArray(docs)) {
      return docs.map(mapDocToCall).filter(Boolean)
    }
    return report?.calls ?? []
  }, [historyData?.response?.docs, report?.calls])

  const barChartData = useMemo(() => {
    const graphic = graphicStatusData || graphicDispositionData
    const buckets = graphic?.buckets
    if (buckets && Array.isArray(buckets) && buckets.length > 0) {
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const labels = buckets.map((b) => {
        if (!b || b.val == null) return '—'
        const raw = String(b.val)
        const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
        if (isoMatch) {
          const [, y, m, day, hh, mm] = isoMatch
          const month = monthNames[parseInt(m, 10)] || m
          return `${parseInt(day, 10)} ${month}, ${hh}:${mm}`
        }
        const d = new Date(b.val)
        if (!Number.isNaN(d.getTime())) {
          const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
          return `${datePart}, ${timePart}`
        }
        return raw
      })
      const getCount = (b) => {
        if (b && typeof b.count === 'number') return b.count
        const byDisp = b?.by_disp?.buckets
        if (Array.isArray(byDisp)) return byDisp.reduce((s, x) => s + (Number(x.count) || 0), 0)
        const byType = b?.by_type?.buckets
        if (Array.isArray(byType)) return byType.reduce((s, x) => s + (Number(x.count) || 0), 0)
        return 0
      }
      const data = buckets.map(getCount)
      return {
        labels,
        datasets: [{
          label: opTr.calls?.[langIndex] ?? 'Calls',
          data,
          backgroundColor: greenTheme.primary,
          borderColor: greenTheme.primary,
          borderWidth: 1,
        }],
      }
    }
    const byDay = {}
    callsForCharts.forEach((c) => {
      const d = c.start ? new Date(c.start).toISOString().slice(0, 10) : ''
      if (d) byDay[d] = (byDay[d] || 0) + 1
    })
    const sorted = Object.entries(byDay).sort((a, b) => a[0].localeCompare(b[0]))
    return {
      labels: sorted.map(([d]) => d),
      datasets: [{
        label: opTr.calls?.[langIndex] ?? 'Calls',
        data: sorted.map(([, n]) => n),
        backgroundColor: greenTheme.primary,
        borderColor: greenTheme.primary,
        borderWidth: 1,
      }],
    }
  }, [graphicStatusData, graphicDispositionData, callsForCharts, opTr, langIndex])

  const directionChartData = useMemo(() => {
    const directionCounts = directionCountsFromStatusFacet(facetFields)
    let data
    if (directionCounts) {
      data = CALL_DIRECTIONS.map((d) => directionCounts[d.key] || 0)
    } else {
      const byDir = {}
      CALL_DIRECTIONS.forEach((d) => { byDir[d.key] = 0 })
      callsForCharts.forEach((c) => {
        const d = normalizeDirection(c)
        if (byDir[d] !== undefined) byDir[d] += 1
      })
      data = CALL_DIRECTIONS.map((d) => byDir[d.key] || 0)
    }
    return {
      labels: CALL_DIRECTIONS.map((d) => opTr[d.labelKey]?.[langIndex] ?? d.key),
      datasets: [{
        label: opTr.calls?.[langIndex] ?? 'Calls',
        data,
        backgroundColor: CALL_DIRECTIONS.map((d) => d.color),
        borderColor: CALL_DIRECTIONS.map((d) => d.color),
        borderWidth: 1,
      }],
    }
  }, [facetFields, callsForCharts, opTr, langIndex])

  return (
    <>
      {showAll && (
        <Grid container spacing={3} sx={opStyles.gridMb}>
          <Grid item xs={12} md={5}>
            <Paper sx={opStyles.chartPaper}>
              <Typography variant="subtitle1" sx={opStyles.chartTitle}>
                {opTr.callsByStatus?.[langIndex]}
              </Typography>
              <Box sx={opStyles.chartBox}>
                <Pie data={pieChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper sx={opStyles.chartPaper}>
              <Typography variant="subtitle1" sx={opStyles.chartTitle}>
                {opTr.callsPerDay?.[langIndex]}
              </Typography>
              <Box sx={opStyles.chartBox}>
                <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { type: 'category', display: true }, y: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      {showDirections && (
        <Box id="section-directions" sx={opStyles.sectionBox}>
          <Grid container spacing={3} sx={opStyles.gridMb}>
            <Grid item xs={12}>
              <Paper sx={opStyles.chartPaperWide}>
                <Typography variant="subtitle1" sx={opStyles.chartTitle}>
                  {opTr.callsByDirection?.[langIndex]}
                </Typography>
                <Box sx={opStyles.chartBoxShort}>
                  <Bar data={directionChartData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } } }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  )
}
